/**
 * FyiQueue — Admin Phase 1c · live self-serve FYI click queue.
 *
 * Founder ruling 14 Jun 2026 PM (later · C9.1 polish pass):
 *   "Strictly read-only. Server-side redacted projection. Role-shaped
 *    visibility. Surfaces FYI click rows from lc_click_intents · no
 *    convert-to-request button (out of scope). UI must use the same
 *    admin shell polish standard as the rest of the Control Room."
 *
 * Reads: GET /api/os-admin/fyi
 *        GET /api/os-admin/fyi/{intent_id}
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const SURFACE_LABELS = {
  enjoy: "Enjoy",
  table: "Table",
  escape: "Escape",
  club: "Club",
  stay: "Stay",
  fly: "Fly",
  shop: "Shop",
  support: "Support",
};

// Rails seen across the four live surfaces (subset of the writer's
// _RAIL_WHITELIST that ships visible chips · other rails still work
// via the multi-OK query param when they appear in data).
const COMMON_RAILS = [
  "ticketmaster", "predicthq", "resy", "sevenrooms", "opentable",
  "tablecheck", "tock", "viator", "getyourguide", "klook",
  "official_site", "venue_direct",
];

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return iso;
  }
}

function shortId(s) {
  if (!s) return "—";
  return s.length > 8 ? s.slice(-8) : s;
}

export default function FyiQueue() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    total: 0, page: 1, per_page: 25,
    role: "read_only", has_more: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    surface: [],
    rail: [],
    has_zoho_ticket: null,
    is_authenticated: null,
    deduped: null,
    q: "",
    page: 1,
    per_page: 25,
  });

  const buildQS = (f) => {
    const p = new URLSearchParams();
    (f.surface || []).forEach((s) => p.append("surface", s));
    (f.rail || []).forEach((r) => p.append("rail", r));
    if (f.has_zoho_ticket === true) p.set("has_zoho_ticket", "true");
    if (f.has_zoho_ticket === false) p.set("has_zoho_ticket", "false");
    if (f.is_authenticated === true) p.set("is_authenticated", "true");
    if (f.is_authenticated === false) p.set("is_authenticated", "false");
    if (f.deduped === true) p.set("deduped", "true");
    if (f.deduped === false) p.set("deduped", "false");
    if ((f.q || "").trim()) p.set("q", f.q.trim());
    p.set("page", String(f.page));
    p.set("per_page", String(f.per_page));
    return p.toString();
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const qs = buildQS(filters);
        const r = await api.get(`/os-admin/fyi?${qs}`);
        if (cancelled) return;
        setRows(r.data.rows || []);
        setMeta({
          total: r.data.total || 0,
          page: r.data.page || 1,
          per_page: r.data.per_page || 25,
          role: r.data.role || "read_only",
          has_more: !!r.data.has_more,
        });
      } catch (e) {
        if (cancelled) return;
        // Show a clean human-readable message, never the raw Axios trace.
        const friendly =
          formatApiError(e?.response?.data?.detail) ||
          "We could not load the FYI feed just now. Please retry in a moment.";
        setError(friendly);
        setRows([]);
        setMeta((m) => ({ ...m, total: 0, has_more: false }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters]);

  const toggleSet = (key, value) => {
    setFilters((prev) => {
      const cur = new Set(prev[key] || []);
      cur.has(value) ? cur.delete(value) : cur.add(value);
      return { ...prev, [key]: Array.from(cur), page: 1 };
    });
  };

  const isReadOnly = meta.role === "read_only";
  const showZohoCol = !isReadOnly;

  return (
    <div className="os-fyi-queue" data-testid="os-fyi-queue">
      <div className="os-pagehead">
        <h1>Self-serve FYI</h1>
        <span className="count">
          {loading ? "Loading…" : `${meta.total} rows`}
        </span>
      </div>
      <div className="os-pagesub">
        Live spine queue · <code>lc_click_intents</code> · role: <strong data-testid="os-fyi-role">{meta.role}</strong> · server-side redaction applied
      </div>

      {/* ── Filter group · Surface ────────────────────────────── */}
      <div className="os-fyi-filterblock" data-testid="os-fyi-filters">
        <div className="os-toolbar">
          <span className="os-fyi-label">Surface</span>
          {Object.entries(SURFACE_LABELS).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={`os-fchip ${filters.surface.includes(k) ? "on" : ""}`}
              onClick={() => toggleSet("surface", k)}
              data-testid={`os-fyi-filter-surface-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="os-toolbar">
          <span className="os-fyi-label">Rail</span>
          {COMMON_RAILS.map((r) => (
            <button
              key={r}
              type="button"
              className={`os-fchip ${filters.rail.includes(r) ? "on" : ""}`}
              onClick={() => toggleSet("rail", r)}
              data-testid={`os-fyi-filter-rail-${r}`}
            >
              {r.replace("_", " ")}
            </button>
          ))}
        </div>

        <div className="os-toolbar">
          <span className="os-fyi-label">Zoho FYI</span>
          {[
            { key: "any", value: null, label: "any" },
            { key: "yes", value: true, label: "fired" },
            { key: "no", value: false, label: "skipped" },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              className={`os-fchip ${filters.has_zoho_ticket === o.value ? "on" : ""}`}
              onClick={() => setFilters((p) => ({ ...p, has_zoho_ticket: o.value, page: 1 }))}
              data-testid={`os-fyi-filter-zoho-${o.key}`}
            >
              {o.label}
            </button>
          ))}

          <span className="os-fyi-label os-fyi-label-spaced">Member</span>
          {[
            { key: "any", value: null, label: "any" },
            { key: "yes", value: true, label: "signed in" },
            { key: "no", value: false, label: "anonymous" },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              className={`os-fchip ${filters.is_authenticated === o.value ? "on" : ""}`}
              onClick={() => setFilters((p) => ({ ...p, is_authenticated: o.value, page: 1 }))}
              data-testid={`os-fyi-filter-auth-${o.key}`}
            >
              {o.label}
            </button>
          ))}

          <span className="os-fyi-label os-fyi-label-spaced">Dedupe</span>
          {[
            { key: "any", value: null, label: "any" },
            { key: "yes", value: true, label: "deduped" },
            { key: "no", value: false, label: "fresh" },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              className={`os-fchip ${filters.deduped === o.value ? "on" : ""}`}
              onClick={() => setFilters((p) => ({ ...p, deduped: o.value, page: 1 }))}
              data-testid={`os-fyi-filter-dedupe-${o.key}`}
            >
              {o.label}
            </button>
          ))}

          <span className="os-spacer" />
          <input
            type="search"
            placeholder="Search title · venue · city · rail · entity id"
            value={filters.q}
            onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value, page: 1 }))}
            maxLength={80}
            className="os-fyi-search"
            data-testid="os-fyi-search-input"
          />
        </div>
      </div>

      {/* ── Table or message ────────────────────────────────── */}
      {error ? (
        <div className="os-fyi-msg os-fyi-msg-err" data-testid="os-fyi-error">
          {error}
        </div>
      ) : rows.length === 0 && !loading ? (
        <div className="os-fyi-msg" data-testid="os-fyi-empty">
          No FYI clicks match these filters.
        </div>
      ) : (
        <table className="os-tbl" data-testid="os-fyi-table">
          <thead>
            <tr>
              <th>Intent</th>
              <th>Surface</th>
              <th>Rail</th>
              <th>Entity</th>
              <th>Member</th>
              <th>Status</th>
              {showZohoCol && <th>Zoho FYI</th>}
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const snap = r.entity_snapshot || {};
              const title = snap.title || snap.provider_name || r.entity_id || "—";
              const city = snap.city || "";
              const intentId = r.intent_id || r._id || "";
              const memberName = r.member_first
                ? `${r.member_first}${r.member_last ? " " + r.member_last : ""}`
                : (r.member_id ? "(member)" : "anonymous");
              return (
                <tr key={intentId} data-testid={`os-fyi-row-${intentId}`}>
                  <td>
                    <span className="os-ref" title={intentId}>{shortId(intentId)}</span>
                  </td>
                  <td>
                    <span className="os-surf">{SURFACE_LABELS[r.surface] || r.surface || "—"}</span>
                  </td>
                  <td>
                    {r.rail
                      ? <span className="os-fyi-rail">{r.rail.replace(/_/g, " ")}</span>
                      : <span className="os-fyi-dim">—</span>}
                  </td>
                  <td>
                    <div className="os-fyi-title">{title}</div>
                    {city && <div className="os-fyi-sub">{city}</div>}
                  </td>
                  <td>{memberName}</td>
                  <td>
                    {r.deduped ? (
                      <span className="os-pill os-p-prog" data-testid={`os-fyi-deduped-${intentId}`}>
                        Deduped
                      </span>
                    ) : r.anonymous_suppressed ? (
                      <span className="os-pill os-p-new" data-testid={`os-fyi-anon-${intentId}`}>
                        Anonymous
                      </span>
                    ) : (
                      <span className="os-pill os-p-done">Fresh</span>
                    )}
                  </td>
                  {showZohoCol && (
                    <td>
                      {r.zoho_fyi_ticket_id ? (
                        <span className="os-ref" data-testid={`os-fyi-zoho-${intentId}`}>
                          {r.zoho_fyi_ticket_id}
                        </span>
                      ) : (
                        <span className="os-fyi-dim">—</span>
                      )}
                    </td>
                  )}
                  <td title={r.created_at}>{relativeTime(r.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── Pagination ───────────────────────────────────────── */}
      <div className="os-fyi-pagi" data-testid="os-fyi-pagination">
        <button
          type="button"
          className="os-btn-ghost"
          disabled={meta.page <= 1 || loading}
          onClick={() => setFilters((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
          data-testid="os-fyi-page-prev"
        >‹ Prev</button>
        <span className="os-fyi-pagemark">
          Page {meta.page} · {meta.per_page}/page
        </span>
        <button
          type="button"
          className="os-btn-ghost"
          disabled={!meta.has_more || loading}
          onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
          data-testid="os-fyi-page-next"
        >Next ›</button>
      </div>
    </div>
  );
}
